import type { ParseKeys } from 'i18next';
import { Season } from '@/types/components';
import { RANGE_SYMBOL, capitalize } from '@/utils/text';

const MS_IN_DAY = 1000 * 60 * 60 * 24;

export const DAYS_IN_WEEK = 7;

const startOfDayMs = (iso: string): number =>
  new Date(iso).setHours(0, 0, 0, 0);

export const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);

  return result;
};

export const startOfToday = (): Date => startOfDay(new Date());

export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addLeadingZeros = (value: number, length: number) =>
  String(value).padStart(length, '0');

export const toApiDate = (date: Date): string =>
  `${date.getFullYear()}-${addLeadingZeros(date.getMonth() + 1, 2)}-${addLeadingZeros(date.getDate(), 2)}`;

export const toApiDateTime = (date: Date): string =>
  `${toApiDate(date)}T${addLeadingZeros(date.getHours(), 2)}:${addLeadingZeros(date.getMinutes(), 2)}:${addLeadingZeros(date.getSeconds(), 2)}.${addLeadingZeros(date.getMilliseconds(), 3)}`;

export const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return `${addLeadingZeros(d.getDate(), 2)}.${addLeadingZeros(d.getMonth() + 1, 2)}.${d.getFullYear()}`;
};

export const formatDatePeriod = (from: string, to: string): string =>
  `${formatDate(from)} ${RANGE_SYMBOL} ${formatDate(to)}`;

export const formatDateRange = (from: string, to: string): string =>
  isSameApiDate(from, to) ? formatDate(from) : formatDatePeriod(from, to);

export const getDatePart = (iso: string): string => iso.slice(0, 10);

export const parseLocalDate = (iso: string): Date => {
  const [year, month, day] = getDatePart(iso).split('-').map(Number);

  return new Date(year, month - 1, day);
};

export const isSameApiDate = (a: string, b: string): boolean =>
  getDatePart(a) === getDatePart(b);

export const doPeriodsOverlap = (
  period: { from: string; to: string },
  otherPeriod: { from: string; to: string },
): boolean =>
  startOfDayMs(period.from) <= startOfDayMs(otherPeriod.to) &&
  startOfDayMs(otherPeriod.from) <= startOfDayMs(period.to);

export const isTodayInPeriod = (fromIso: string, toIso: string): boolean => {
  const today = startOfToday().getTime();

  return today >= startOfDayMs(fromIso) && today <= startOfDayMs(toIso);
};

const shortDateFormatter = new Intl.DateTimeFormat('lv-LV', {
  day: 'numeric',
  month: 'short',
});

const formatDayMonth = (iso: string): string => {
  const date = new Date(iso);

  return `${addLeadingZeros(date.getDate(), 2)}.${addLeadingZeros(date.getMonth() + 1, 2)}.`;
};

export const formatShortDate = (iso: string): string =>
  shortDateFormatter.format(new Date(iso));

export const formatTime = (iso: string): string => {
  const date = new Date(iso);

  return `${addLeadingZeros(date.getHours(), 2)}:${addLeadingZeros(date.getMinutes(), 2)}`;
};

const shortMonthFormatter = new Intl.DateTimeFormat('lv-LV', {
  month: 'short',
});

export const formatDayShortMonthYear = (iso: string): string => {
  const date = new Date(iso);
  const day = addLeadingZeros(date.getDate(), 2);

  return `${day}.${shortMonthFormatter.format(date)} ${date.getFullYear()}`;
};

export const formatDayShortMonthYearTime = (iso: string): string => {
  const date = new Date(iso);
  const hour = addLeadingZeros(date.getHours(), 2);
  const minute = addLeadingZeros(date.getMinutes(), 2);

  return `${formatDayShortMonthYear(iso)} ${hour}:${minute}`;
};

const longDateFormatter = new Intl.DateTimeFormat('lv-LV', {
  day: 'numeric',
  month: 'long',
});

export const formatLongDate = (iso: string): string =>
  longDateFormatter.format(new Date(iso));

export const isToday = (iso: string): boolean =>
  isSameDay(new Date(iso), startOfToday());

export const isYesterday = (iso: string): boolean =>
  isSameDay(new Date(iso), addDays(startOfToday(), -1));

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const narrowWeekdayFormatter = new Intl.DateTimeFormat('lv-LV', {
  weekday: 'narrow',
});

export const formatNarrowWeekday = (date: Date): string =>
  narrowWeekdayFormatter.format(date);

export const isWeekend = (date: Date): boolean =>
  date.getDay() === 0 || date.getDay() === 6;

export const getWeekRangeWithin = ({
  date,
  from,
  to,
}: {
  date: Date;
  from?: Date;
  to?: Date;
}): { start: Date; end: Date } => {
  const start = startOfWeek(date);
  const end = endOfWeek(date);

  return {
    start: from && start < from ? from : start,
    end: to && end > to ? to : end,
  };
};

export const clampDate = ({
  date,
  from,
  to,
}: {
  date: Date;
  from: Date;
  to: Date;
}): Date => {
  if (date < from) {
    return from;
  }

  return date > to ? to : date;
};

export const getDatesInRange = (from: Date, to: Date): Date[] => {
  const dates: Date[] = [];
  const lastDate = startOfDay(to);

  for (let date = startOfDay(from); date <= lastDate; date = addDays(date, 1)) {
    dates.push(date);
  }

  return dates;
};

export const startOfWeek = (date: Date): Date => {
  const mondayBasedWeekday = (date.getDay() + 6) % DAYS_IN_WEEK;

  return addDays(date, -mondayBasedWeekday);
};

export const endOfWeek = (date: Date): Date =>
  addDays(startOfWeek(date), DAYS_IN_WEEK - 1);

export const formatWeekRange = (start: Date, end: Date): string => {
  const endFormatted = longDateFormatter.format(end);

  if (isSameDay(start, end)) {
    return endFormatted;
  }

  const isWithinOneMonth =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();
  const startFormatted = isWithinOneMonth
    ? `${start.getDate()}.`
    : longDateFormatter.format(start);

  return `${startFormatted} ${RANGE_SYMBOL} ${endFormatted}`;
};

export const sortByDateDesc = <T>(
  items: T[],
  getDate: (item: T) => string,
): T[] =>
  items
    .slice()
    .sort(
      (first, second) =>
        new Date(getDate(second)).getTime() -
        new Date(getDate(first)).getTime(),
    );

const monthFormatter = new Intl.DateTimeFormat('lv-LV', { month: 'long' });

export const formatMonth = (iso: string): string =>
  monthFormatter.format(new Date(iso));

export const formatFullMonth = (date: Date): string =>
  capitalize(monthFormatter.format(date));

export const formatMonthYear = (date: Date): string =>
  `${formatFullMonth(date)} ${date.getFullYear()}`;

const weekdayFormatter = new Intl.DateTimeFormat('lv-LV', { weekday: 'long' });

export const formatDiaryDate = (iso: string): string => {
  const date = new Date(iso);
  const day = addLeadingZeros(date.getDate(), 2);
  const month = addLeadingZeros(date.getMonth() + 1, 2);
  const weekday = weekdayFormatter.format(date).toLowerCase();

  return `${day}.${month}.${date.getFullYear()}, ${weekday}`;
};

export const formatDateWithWeekday = (iso: string): string =>
  `${capitalize(weekdayFormatter.format(new Date(iso)))}, ${formatLongDate(iso)}`;

export const getRelativeDayKey = (date: Date): ParseKeys | undefined => {
  const today = startOfToday();

  if (isSameDay(date, today)) {
    return 'diary.relativeDay.today';
  }

  if (isSameDay(date, addDays(today, -1))) {
    return 'diary.relativeDay.yesterday';
  }

  return isSameDay(date, addDays(today, 1))
    ? 'diary.relativeDay.tomorrow'
    : undefined;
};

export const formatWeekdayShortDate = (iso: string): string => {
  const date = new Date(iso);
  const weekday = capitalize(weekdayFormatter.format(date));
  const month = shortMonthFormatter.format(date);

  return `${weekday}, ${date.getDate()}. ${month}`;
};

export const formatDayMonthWeekday = (iso: string): string => {
  const date = new Date(iso);
  const month = capitalize(monthFormatter.format(date));
  const weekday = capitalize(weekdayFormatter.format(date));

  return `${date.getDate()}. ${month}, ${weekday}`;
};

export const formatWeekdayDayMonth = (iso: string): string => {
  const date = parseLocalDate(iso);
  const weekday = capitalize(weekdayFormatter.format(date));

  return `${weekday}, ${date.getDate()}. ${monthFormatter.format(date)}`;
};

export const formatDayMonthTime = (iso: string): string => {
  const date = new Date(iso);
  const month = capitalize(shortMonthFormatter.format(date));

  return `${date.getDate()}. ${month}, ${formatTime(iso)}`;
};

export const formatShortDateTime = (iso: string): string =>
  `${formatDayMonth(iso)} ${formatTime(iso)}`;

export const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${date.getFullYear()} ${hour}:${minute}`;
};

export const countDaysInclusive = (fromIso: string, toIso: string): number =>
  Math.round((startOfDayMs(toIso) - startOfDayMs(fromIso)) / MS_IN_DAY) + 1;

export const groupByMonth = <T>(
  items: T[],
  getDate: (item: T) => string,
): Map<string, T[]> => {
  const groups = new Map<string, T[]>();

  for (const item of items) {
    const date = new Date(getDate(item));
    const key = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;
    const existing = groups.get(key) ?? [];
    existing.push(item);
    groups.set(key, existing);
  }

  return groups;
};

export const calculateDaysRemaining = (iso: string): number => {
  const end = new Date(iso);

  return Math.trunc((end.getTime() - Date.now()) / MS_IN_DAY) + 1;
};

export const getSeasonForMonth = (month: number): Season => {
  if (month === 12 || month === 1 || month === 2) {
    return Season.Winter;
  }

  if (month >= 3 && month <= 5) {
    return Season.Spring;
  }

  if (month >= 6 && month <= 8) {
    return Season.Summer;
  }

  return Season.Autumn;
};
